import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import { Select, Option } from '@workramp/ui';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import ColorUtils from '~/common/color-utils';
import { media } from '../../../styles';
import AcademiesApi from '../../../agent/academies';
import { isValidUrl, setCookie } from '../../../common';
import academyStyles from './academy-styles';

export const handleLanguageSelect = (selectedLanguageCode) => {
  const { APP_CONSTANTS, getExternalUser } = window;
  const currentUser = getExternalUser();

  if (!Object.keys(APP_CONSTANTS.AVAILABLE_LANGUAGES).includes(selectedLanguageCode)) {
    throw new Error(`${selectedLanguageCode} is not a valid language code`);
  }

  if (currentUser?.language === selectedLanguageCode) return;

  if (currentUser) {
    AcademiesApi.updateExternalUser(currentUser.id, { language: selectedLanguageCode }).then((_updatedUser) => {
      window.location.reload();
    });
  } else {
    setCookie(APP_CONSTANTS.USER_LANGUAGE_COOKIE, selectedLanguageCode, APP_CONSTANTS.USER_LANGUAGE_COOKIE_EXPIRES_IN);
    window.location.reload();
  }
};

/* Globally set so admins can apply the correct language based on a user's custom property */
window.handleLanguageSelect = handleLanguageSelect;

export default class AcademyThemeFooter extends Component {
  TRANSLATED_POWERED_BY = I18n.t('js.academy.footer.powered_by');

  render() {
    const footerLinks =
      this.props.footerLinks.length > 0 &&
      this.props.footerLinks
        .filter((link) => isValidUrl(link.link, ['https', 'mailto']))
        .map((footerLink) => this.renderNavigationLinks(footerLink));

    const languages = _.map(APP_CONSTANTS.AVAILABLE_LANGUAGES, (displayText, languageCode) => (
      <Option key={languageCode} textValue={displayText}>
        <span lang={APP_CONSTANTS.LOCALE_TO_LANGUAGE_CODE_MAP[languageCode]}>{displayText}</span>
      </Option>
    ));

    const showLanguagePicker = !this.props.hideFooterLanguagePicker;

    // _HAS_INTERCOM_INTEGRATION is defined in _academy_integrations_head_end.html.erb if an academy has the intercom integration enabled
    return (
      <AcademyFooter
        backgroundColor={this.props.backgroundColor}
        className={`academy-footer ${academyCustomCSS.footer.bar}`}
      >
        {/* eslint-disable-next-line no-underscore-dangle */}
        <AcademyFooterInner hasIntercomIntegration={window._HAS_INTERCOM_INTEGRATION} textColor={this.props.textColor}>
          <div className="footer-left">
            {footerLinks && (
              <nav className={`academy-nav ${academyCustomCSS.footer.links}`}>
                <ul>{footerLinks}</ul>
              </nav>
            )}
            {footerLinks && this.props.footerText && <FooterLinkTextSpacer />}
            {this.props.footerText && <p>{this.props.footerText}</p>}
          </div>
          <div className="footer-right">
            {showLanguagePicker && (
              <LanguagePickerWrapper>
                <Select
                  className="mrm footer-select"
                  aria-label="Language Selector"
                  label="Language Selector"
                  title="Language Selector"
                  selectedKey={this.props.currentLanguage}
                  onSelectionChange={handleLanguageSelect}
                  size="md"
                  hideLabel
                >
                  {languages}
                </Select>
              </LanguagePickerWrapper>
            )}
            {this.props.showFooterCredits && (
              <div className={`powered-by ${academyCustomCSS.footer.poweredBy}`}>
                <p>{this.TRANSLATED_POWERED_BY}</p>
                <a
                  href={APP_CONSTANTS.ACADEMY_FOOTER_POWERED_BY_LINK}
                  target="_blank"
                  aria-label="WorkRamp"
                  rel="noreferrer"
                >
                  <img
                    src={
                      ColorUtils.getTextColorForHex(this.props.backgroundColor) === 'white'
                        ? '/images/workramp_logo_all_white.png'
                        : '/images/workramp_logo_all_grey.png'
                    }
                    alt=""
                  />
                </a>
              </div>
            )}
          </div>
        </AcademyFooterInner>
      </AcademyFooter>
    );
  }

  renderNavigationLinks = (link) => (
    <li key={link.link} id={link.link}>
      <a
        className={academyCustomCSS.footer.link}
        href={link.link}
        target={link.shouldOpenInNewWindow || _.isUndefined(link.shouldOpenInNewWindow) ? '_blank' : '_self'}
        rel="noreferrer"
      >
        {link.linkText}
      </a>
    </li>
  );
}

const AcademyFooter = styled.div`
  background-color: ${(props) => props.backgroundColor};

  @media print {
    display: none;
  }
`;

const AcademyFooterInner = styled.div`
  max-width: ${academyStyles.common.maxWidth}px;
  margin: 0 auto;
  font-size: 0.875rem;
  padding: 15px 2rem;
  display: flex;
  justify-content: space-between;
  color: ${(props) => props.textColor};
  a {
    color: ${(props) => props.textColor} !important;
  }
  ${media.mobile} {
    flex-wrap: wrap;
  }
  .footer-left {
    max-width: 49%;
    ${media.mobile} {
      max-width: 100%;
      width: 100%;
    }
    p {
      margin: 0;
      ${media.mobile} {
        margin-bottom: 40px;
      }
    }
    ul {
      margin: 0;
      padding: 0;
      list-style: none;
      list-style-type: none;
      list-style-image: none;
      ${media.mobile} {
        margin-bottom: 40px;
      }
      li {
        display: inline;
        a {
          display: inline-block;
          padding: 0 20px;
          text-decoration: underline;
          ${media.mobile} {
            display: block;
            padding: 5px 0;
          }
        }
        &:first-child {
          a {
            padding-left: 0;
          }
        }
        ${media.mobile} {
          display: block;
        }
      }
    }
  }
  .footer-right {
    max-width: 49%;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    ${(props) =>
      props.hasIntercomIntegration &&
      `
        @media (max-width: ${academyStyles.common.maxWidth + 175}px) {
          padding-right: 70px;
        }
      `}
    ${media.mobile} {
      max-width: 100%;
      width: 100%;
      align-items: flex-start;
      margin-left: 0;
      padding-right: 0;
    }

    .footer-select {
      &:focus-within {
        outline: -webkit-focus-ring-color auto 1px;
      }
    }

    .powered-by {
      display: flex;
      align-items: center;
      ${media.mobile} {
        width: 100%;
      }
      p {
        margin: 0;
      }
      img {
        width: 135px;
        height: auto;
        display: inline-block;
        margin: 0 15px;
      }
      ${media.mobile} {
        flex-direction: column;
        align-items: flex-start;
        img {
          margin: 5px 0 0;
        }
      }
    }
  }
`;

const FooterLinkTextSpacer = styled.div`
  height: 15px;
  ${media.mobile} {
    display: none;
  }
`;

const LanguagePickerWrapper = styled.div`
  margin-bottom: 13px;
`;
